import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { Campaign } from "@/types/campaign";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  Settings,
  ChevronDown,
  MoreVertical,
  Trash2
} from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { CampaignCard } from "@/components/brand-dashboard/CampaignCard";
import { format } from "date-fns";
import { getCampaignRedirectUrl, getCampaignActionText } from "@/utils/campaignRedirects";

// Import Artie logo
import artieLogo from "@/assets/images/artie.png";

export default function CampaignsListPage() {
  const { user, brand, logout } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const ITEMS_PER_PAGE = 9;

  const brandId = brand?.id || brand?._id;

  const { data: campaigns = [], isLoading } = useQuery<Campaign[]>({
    queryKey: ["brandCampaigns", brandId],
    queryFn: () => apiClient.getBrandCampaigns(brandId!),
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch brand packages to determine package availability
  const { data: brandPackages = [], isLoading: isLoadingPackages } = useQuery<any[]>({
    queryKey: ["brandPackages", brandId],
    queryFn: async () => {
      const response = await apiClient.getBrandPackages(brandId!, "active");
      return response.packages || [];
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
  });

  // Create a map of campaign_id to packages for easy lookup
  const packagesByCampaign = brandPackages.reduce((acc, pkg) => {
    if (pkg.campaign_id) {
      if (!acc[pkg.campaign_id]) {
        acc[pkg.campaign_id] = [];
      }
      acc[pkg.campaign_id].push(pkg);
    }
    return acc;
  }, {} as Record<string, any[]>);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isProfileDropdownOpen) {
        const target = event.target as HTMLElement;
        if (!target.closest('.profile-dropdown')) {
          setIsProfileDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileDropdownOpen]);

  // Filtering Logic - exclude cancelled campaigns by default unless specifically filtered for
  const filteredCampaigns = campaigns.filter(campaign => {
    // Filter out cancelled campaigns unless user specifically selects "cancelled" status
    if (campaign.status === 'cancelled' && statusFilter !== 'cancelled') {
      return false;
    }
    
    const matchesSearch = campaign.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination Logic
  const totalPages = Math.ceil(filteredCampaigns.length / ITEMS_PER_PAGE);
  const paginatedCampaigns = filteredCampaigns.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'packaging':
        return 'bg-yellow-100 text-yellow-800';
      case 'presented':
        return 'bg-indigo-100 text-indigo-800';
      case 'hold':
        return 'bg-orange-100 text-orange-800';
      case 'complete':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDeleteClick = (campaign: Campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteDialog(true);
  };

  const handleDeleteCampaign = async () => {
    if (!campaignToDelete) return;
    
    setIsDeleting(true);
    try {
      // Update campaign status to 'cancelled' instead of deleting
      await apiClient.updateCampaign(campaignToDelete.id, { status: 'cancelled' });
      
      // Invalidate and refetch campaigns data
      queryClient.invalidateQueries({ queryKey: ['brandCampaigns'] });
      
      setShowDeleteDialog(false);
      setCampaignToDelete(null);
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Prevent deletion when hold is placed or for certain statuses
  const canDelete = (campaign: Campaign) => campaign.status !== 'hold' && !['live', 'completed'].includes(campaign.status);

  return (
    <div className="min-h-screen bg-white font-geist">
      {/* Top Navigation */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side - Logo */}
            <Link to="/brand-dashboard" className="flex items-center hover:opacity-80 transition-opacity">
              <img src={artieLogo} alt="Artie" className="w-10 h-10 mr-3" />
              <span className="font-work font-semibold text-3xl text-neutral-900 tracking-normal">artie</span>
            </Link>
            
            {/* Center - Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link to="/brand-dashboard" className="text-neutral-600 hover:text-neutral-900 font-medium">
                Home
              </Link>
              <Link to="/campaigns" className="text-neutral-900 font-semibold">
                Campaigns
              </Link>
              <span className="text-neutral-600 hover:text-neutral-900 font-medium cursor-pointer">IO & Payments</span>
              <span className="text-neutral-600 hover:text-neutral-900 font-medium cursor-pointer">Settings</span>
            </nav>
            
            {/* Right side - User info */}
            <div className="flex items-center space-x-4">
              <div className="relative profile-dropdown">
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center space-x-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                >
                  <span className="bg-neutral-100 px-3 py-1 rounded-full font-medium">
                    {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown Menu */}
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-neutral-200 rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-neutral-100">
                        <p className="text-sm font-medium text-neutral-900">{user?.name || 'User'}</p>
                        <p className="text-xs text-neutral-500">{user?.email || 'user@example.com'}</p>
                      </div>
                      <div className="pt-2">
                        <button
                          onClick={() => {
                            setIsProfileDropdownOpen(false);
                            logout();
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-4 h-4 mr-3" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">Your Campaigns</h1>
            <p className="text-neutral-500 mt-1">Manage and track all your brand campaigns</p>
          </div>
          <div className="flex gap-3">
            <Link to="/campaigns/booking-windows">
              <Button className="bg-neutral-900 text-white hover:bg-neutral-800 hover:shadow-lg transition-all">
                <Plus className="w-4 h-4 mr-2" />
                Create New Campaign
              </Button>
            </Link>
          </div>
        </div>

        {/* Search and Filter Controls */}
        {!isLoading && campaigns.length > 0 && (
          <div className="mb-8 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-500" />
              <Input
                type="text"
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-neutral-200 focus:border-neutral-400 focus:ring-neutral-400"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 border-neutral-200 focus:border-neutral-400 focus:ring-neutral-400">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="packaging">Packaging</SelectItem>
                <SelectItem value="presented">Presented</SelectItem>
                <SelectItem value="hold">Hold</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
            <span className="ml-3 text-neutral-600">Loading campaigns...</span>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && campaigns.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-neutral-100 rounded-full flex items-center justify-center">
              <Plus className="w-8 h-8 text-neutral-400" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">No campaigns yet</h3>
            <p className="text-neutral-500 text-sm mb-6">Create your first campaign to get started with Artie.</p>
            <Link to="/campaigns/booking-windows">
              <Button className="bg-neutral-900 text-white hover:bg-neutral-800">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Campaign
              </Button>
            </Link>
          </div>
        )}

        {/* No Results State */}
        {!isLoading && campaigns.length > 0 && filteredCampaigns.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-neutral-100 rounded-full flex items-center justify-center">
              <Search className="w-8 h-8 text-neutral-400" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">No campaigns found</h3>
            <p className="text-neutral-500 text-sm mb-6">Try adjusting your search or filter criteria.</p>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("all");
              }}
              className="border-neutral-200 hover:bg-neutral-50"
            >
              Clear Filters
            </Button>
          </div>
        )}

        {/* Campaigns Grid */}
        {!isLoading && paginatedCampaigns.length > 0 && (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
              {paginatedCampaigns.map((campaign) => {
                const campaignPackages = packagesByCampaign[campaign.id] || [];
                const hasPackages = campaignPackages.length > 0 && campaignPackages.some(pkg => pkg.status === 'active');
                
                return (
                  <Card key={campaign.id} className="hover:shadow-lg transition-all duration-200 cursor-pointer border-neutral-200">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg font-semibold text-neutral-900 line-clamp-2">
                            {campaign.name}
                          </CardTitle>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={`${getStatusColor(campaign.status)} border-0`}>
                              {campaign.status}
                            </Badge>
                            {hasPackages && (
                              <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
                                Package Available
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {canDelete(campaign) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-neutral-400 hover:text-black hover:bg-neutral-50 shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(campaign);
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Campaign
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-500">Budget:</span>
                          <span className="font-medium text-neutral-900">
                            ${campaign.requirements?.budget_usd?.toLocaleString() || '0'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-500">Created:</span>
                          <span className="text-neutral-700">
                            {format(new Date(campaign.created_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                        {campaign.window_details && (
                          <div className="flex justify-between text-sm">
                            <span className="text-neutral-500">Window:</span>
                            <span className="text-neutral-700">{campaign.window_details.name}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-4 pt-4 border-t border-neutral-100">
                        <Button
                          className="w-full bg-neutral-900 text-white hover:bg-neutral-800"
                          onClick={() => navigate(getCampaignRedirectUrl(campaign, hasPackages))}
                        >
                          {getCampaignActionText(campaign, hasPackages)}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-neutral-500">
                  Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredCampaigns.length)} of {filteredCampaigns.length} campaigns
                </p>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="border-neutral-200"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-neutral-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="border-neutral-200"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Campaign</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{campaignToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCampaign}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Campaign'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}